'use client';

import { forwardRef } from 'react';
import { cn } from '@/shared/lib/cn';
import {
  SettingsItem,
  SettingsDisplayProvider,
  useSettingsDisplay,
} from '@/shared/ui/settings';

// The settings-display context and the row primitive now live in
// `@/shared/ui/settings` so every settings surface (render inspector, album,
// estimate, account) renders through ONE implementation. These re-exports keep
// the render-side import paths stable.
export { SettingsDisplayProvider, useSettingsDisplay };

interface SettingsSectionRowProps {
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  title: string;
  subtitle?: string | string[];
  /** Rich inline subtitle (e.g. reference thumbnail strip). Takes precedence
   *  over the plain-text `subtitle` in the trailing slot when provided. */
  subtitleContent?: React.ReactNode;
  /** Rich block rendered BELOW the title, which wraps instead of truncating —
   *  for a selection that has to be readable in full. See `SettingsItem`. */
  stackedContent?: React.ReactNode;
  hasChanges?: boolean;
  onClick?: () => void;
  className?: string;
  /** Optional element rendered right next to the title (e.g. a PRO badge). */
  titleAdornment?: React.ReactNode;
  /** Orange dot signalling the row needs user attention (e.g. a required
   *  field is empty). Mirrors the iOS/Android indicator. */
  needsAttention?: boolean;
}

/**
 * Render-inspector settings row — a thin adapter over the shared `SettingsItem`
 * primitive. Kept as a named wrapper so the ~10 render section components don't
 * need to change their import; the compact inspector uses the `px-4` gutter.
 */
export const SettingsSectionRow = forwardRef<
  HTMLDivElement,
  SettingsSectionRowProps
>(
  (
    {
      icon,
      title,
      subtitle,
      subtitleContent,
      stackedContent,
      hasChanges = false,
      onClick,
      className,
      titleAdornment,
      needsAttention = false,
    },
    ref
  ) => (
    <SettingsItem
      ref={ref}
      icon={icon}
      title={title}
      titleAdornment={titleAdornment}
      value={typeof subtitle === 'string' ? subtitle : undefined}
      subtitleLines={Array.isArray(subtitle) ? subtitle : undefined}
      valueContent={subtitleContent}
      stackedContent={stackedContent}
      hasChanges={hasChanges}
      needsAttention={needsAttention}
      onClick={onClick}
      className={className}
      paddingClassName="px-4 py-3"
    />
  )
);

SettingsSectionRow.displayName = 'SettingsSectionRow';

interface SettingsSectionProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  subtitle?: string | string[];
  hasChanges?: boolean;
  onClick?: () => void;
  children?: React.ReactNode;
  className?: string;
  showChevron?: boolean;
  compact?: boolean;
  hideIcon?: boolean;
}

/**
 * Legacy expandable settings section (icon tile + title + inline children).
 * Distinct from the menu-style `SettingsSectionRow` — it renders a child block
 * below the header instead of a trailing value. Retained for the few callers
 * that still expand content in place.
 */
export const SettingsSection = forwardRef<HTMLDivElement, SettingsSectionProps>(
  (
    {
      icon: Icon,
      title,
      subtitle,
      hasChanges = false,
      onClick,
      children,
      className,
      showChevron: _showChevron = false,
      compact = false,
      hideIcon = false,
    },
    ref
  ) => {
    const isClickable = !!onClick;
    const { hideIcons: hideIconsFromContext } = useSettingsDisplay();
    const shouldHideIcon = hideIcon || hideIconsFromContext;

    return (
      <div
        ref={ref}
        className={cn(
          'border-b border-border/40 last:border-b-0',
          isClickable &&
            'cursor-pointer hover:bg-foreground/5 active:bg-foreground/10',
          className
        )}
        onClick={onClick}
        role={isClickable ? 'button' : undefined}
        tabIndex={isClickable ? 0 : undefined}
        onKeyDown={(e) => {
          if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            onClick?.();
          }
        }}
      >
        <div
          className={cn('flex items-center gap-3', compact ? 'px-4 py-3' : 'px-4 py-3')}
        >
          {!shouldHideIcon && (
            <div className="flex-shrink-0 w-10 h-10 rounded-[10px] bg-muted flex items-center justify-center">
              <Icon className="w-5 h-5 text-muted-foreground" />
            </div>
          )}

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-settings-row-title text-foreground">
                {title}
              </span>
              {hasChanges && (
                <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
              )}
              <span className="flex-1" />
              {subtitle && !Array.isArray(subtitle) && (
                <span className="text-settings-row-value text-muted-foreground max-w-[120px] truncate">
                  {subtitle}
                </span>
              )}
            </div>

            {subtitle && Array.isArray(subtitle) && (
              <div className="flex flex-col gap-1.5 mt-1">
                {subtitle.map((line, i) => (
                  <p
                    key={i}
                    className="text-settings-row-value text-muted-foreground line-clamp-2"
                  >
                    {line}
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>

        {children && <div className="px-4 pb-4">{children}</div>}
      </div>
    );
  }
);

SettingsSection.displayName = 'SettingsSection';
